import React, { useEffect, useMemo, useState } from 'react';
import { useStoredHeight, MIN_STORED_HEIGHT } from '../../../lib/useStoredHeight';
import { useTranslation } from 'react-i18next';
import { Badge, Card, Button, Avatar } from '../../../components/ui';
import { Icon } from '../../../components/icons';
import { BRAND, getThemeColors, DENSITY_VALUES } from '../../../lib/theme';
import { v1Admins, v1UserToCaregiver, type V1Admin } from '../../../lib/v1';
import { bkList } from '../../../lib/bkList';
import { AdminMultiFilter } from '../../../components/AdminMultiFilter';
import {
  BK_AVAILABILITY_SELECTABLE, primaryEntryByUser, availableFromByUser, isAbsenceType, v2BkAvailability,
  type BkAvailabilityEntry, type BkAvailabilityType,
} from '../../../lib/bkAvailability';
import { buildPath } from '../../../lib/route';
import { AppLink, rowLinkProps } from '../../../lib/openLink';
import { useAuth } from '../../../contexts/AuthContext';
import { useColumnPreferences } from '../../../lib/useColumnPreferences';
import { useIsMobile } from '../../../lib/useBreakpoint';
import { ColumnPicker } from '../../../components/ColumnPicker';
import type { Caregiver, Density } from '../../../types';
import {
  STATUS_TONE, AVATAR_TONE, STATUS_LABEL_KEY,
  FILTERS, BETREUUNG_COLUMNS, BETREUUNG_DEFAULT_COLS,
  BETREUUNG_PER_PAGE, BETREUUNG_SORT_FIELDS,
  BETREUUNG_BOX_HEIGHT_KEY, BETREUUNG_BOX_DEFAULT_HEIGHT,
} from './constants';
import { renderCaregiverCell } from './CaregiverCell';
import { CaregiverDetail } from './CaregiverDetail';

interface BetreuungPageProps {
  dark: boolean;
  density: Density;
}

export function BetreuungPage({ dark, density }: BetreuungPageProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const d = DENSITY_VALUES[density];
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [items, setItems] = useState<Caregiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Caregiver | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('Alle');
  // Dispo: Verfügbarkeits-Einträge (laufend oder künftig) aller BK. Speist die
  // Spalte „Verfügbarkeit (Dispo)" und den Verfügbarkeits-Filter.
  const [dispoFilter, setDispoFilter] = useState<BkAvailabilityType | 'all'>('all');
  const [dispoEntries, setDispoEntries] = useState<BkAvailabilityEntry[]>([]);
  const [dispoError, setDispoError] = useState(false);
  useEffect(() => {
    let alive = true;
    v2BkAvailability.list({ scope: 'open', limit: 500 })
      .then(rows => { if (alive) { setDispoEntries(rows); setDispoError(false); } })
      .catch(() => { if (alive) setDispoError(true); });
    return () => { alive = false; };
  }, []);
  // Serverseitige Filter: Zugewiesen an (Mehrfachauswahl, gleiche Komponente
  // wie in /pflege) und SGB5 aktiv (profiles.sgb5_bk).
  const [assignedToFilter, setAssignedToFilter] = useState<number[]>([]);
  const [sgb5Only, setSgb5Only] = useState(false);
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  useEffect(() => {
    let alive = true;
    v1Admins.list()
      .then(list => { if (alive) setAdmins(list); })
      .catch(() => { /* Filter bleibt ohne Optionen nutzbar (Alle) */ });
    return () => { alive = false; };
  }, []);
  // Debounced Suchbegriff → serverseitige Suche (filters.search). Pagination
  // über page/last_page; "Mehr laden" hängt an.
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // Filter-Objekt für die BK-Liste (Seite 1 und "Mehr laden").
  const listFilters = useMemo(() => {
    const f: Record<string, string | boolean | number[]> = {};
    if (query) f.search = query;
    if (assignedToFilter.length) f.assigned_to = assignedToFilter;
    if (sgb5Only) f.sgb5_bk = true;
    return Object.keys(f).length ? f : undefined;
  }, [query, assignedToFilter, sgb5Only]);
  // Sortierung (serverseitig). Default: neueste zuerst (id, absteigend).
  const [sortField, setSortField] = useState('id');
  const [sortProfile, setSortProfile] = useState(false);
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1);

  const toggleSort = (colId: string) => {
    const sf = BETREUUNG_SORT_FIELDS[colId];
    if (!sf) return;
    if (sortField === sf.field && sortProfile === !!sf.profile) {
      setSortOrder(o => (o === 1 ? -1 : 1));
    } else {
      setSortField(sf.field);
      setSortProfile(!!sf.profile);
      setSortOrder(1);
    }
  };
  const [visibleCols, setVisibleCols] = useColumnPreferences(
    'betreuung.table',
    user?.id ?? null,
    BETREUUNG_DEFAULT_COLS,
  );
  const translatedColumns = BETREUUNG_COLUMNS.map(c => ({ ...c, label: tr(c.labelKey) }));
  // Bei aktivem Verfügbarkeits-Filter die Dispo-Spalte immer zeigen.
  const cols = translatedColumns.filter(c => visibleCols.includes(c.id) || (c.id === 'dispo' && dispoFilter !== 'all'));

  // Debounce die Sucheingabe (350 ms) in `query`.
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  // Lädt Seite 1 neu, sobald sich der (debounced) Suchbegriff ändert. Die
  // serverseitige Suche findet ALLE BK, nicht nur die geladenen.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await bkList.list({
          page: 1, perPage: BETREUUNG_PER_PAGE,
          filters: listFilters,
          orderBy: { field: sortField, profile: sortProfile }, order: sortOrder,
        });
        if (!alive) return;
        setItems(res.data.map(v1UserToCaregiver));
        setPage(res.current_page);
        setLastPage(res.last_page);
        setTotal(res.total);
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : tr('betreuung.error.load'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [listFilters, sortField, sortProfile, sortOrder]);

  // Hängt die nächste Seite an (behält Suche/Sortierung bei).
  const loadMore = async () => {
    if (loadingMore || page >= lastPage) return;
    setLoadingMore(true);
    try {
      const res = await bkList.list({
        page: page + 1, perPage: BETREUUNG_PER_PAGE,
        filters: listFilters,
        orderBy: { field: sortField, profile: sortProfile }, order: sortOrder,
      });
      setItems(prev => [...prev, ...res.data.map(v1UserToCaregiver)]);
      setPage(res.current_page);
      setLastPage(res.last_page);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('betreuung.error.load'));
    } finally {
      setLoadingMore(false);
    }
  };

  // Dispo-Eintrag je BK anhängen (laufend, sonst der nächste).
  const dispoByUser = primaryEntryByUser(dispoEntries);
  const availableFromByBk = availableFromByUser(dispoEntries);
  const withDispo = (c: Caregiver): Caregiver => {
    const uid = Number(c.id.replace(/^BK-/, ''));
    return { ...c, dispo: dispoByUser.get(uid) ?? null, availableFrom: c.availableFrom !== undefined ? c.availableFrom : (availableFromByBk.get(uid) ?? null) };
  };

  // Verfügbarkeits-Filter aktiv: die Liste kommt aus den Dispo-Einträgen
  // dieser Art (ALLE betroffenen BK, nicht nur die bisher geladenen Seiten);
  // die Suche verfeinert sie clientseitig über Name/E-Mail.
  const dispoActive = dispoFilter !== 'all';
  const dispoRows: Caregiver[] = dispoActive
    ? Array.from(primaryEntryByUser(dispoEntries.filter(e => (dispoFilter === 'abwesenheit' ? isAbsenceType(e.type) : e.type === dispoFilter))).values())
        .filter(e => e.user != null)
        .map(e => ({ ...v1UserToCaregiver(e.user!), dispo: e, availableFrom: availableFromByBk.get(e.user_id) ?? null }))
        .filter(c => !query || `${c.name} ${c.email}`.toLowerCase().includes(query.toLowerCase()))
        // „Zugewiesen an" und „SGB5 aktiv" wirken auch hier (UND), clientseitig.
        .filter(c => assignedToFilter.length === 0 || (c.assigned_to != null && assignedToFilter.includes(c.assigned_to)))
        .filter(c => !sgb5Only || c.sgb5_bk === true)
    : [];

  // Suche läuft serverseitig; hier nur noch der Status-Filter als
  // clientseitige Verfeinerung über die geladenen Seiten.
  const filtered = (dispoActive ? dispoRows : items.map(withDispo)).filter(c =>
    filter === 'Alle' || c.status === filter
  );

  // A7: Höhe der Tabellen-Box (Drag-Resize + localStorage).
  const { ref: boxRef, height: boxHeight } = useStoredHeight<HTMLDivElement>(BETREUUNG_BOX_HEIGHT_KEY, BETREUUNG_BOX_DEFAULT_HEIGHT);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap, minHeight: '100%' }}>
      {error && (
        <Card dark={dark} style={{ padding: 12, borderColor: '#f04438' }}>
          <div style={{ fontSize: 13, color: '#b42318', fontWeight: 500 }}>
            {tr('betreuung.error.load')}: {error}
          </div>
        </Card>
      )}

      {/* Toolbar — Mobile (<768px): Suche full-width oben, Filter-Pills
          wrappen darunter, View-Toggle + Primary-Action am Ende. Desktop:
          alles in einer Zeile mit Actions ganz rechts via margin-auto. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
          <span style={{ position: 'absolute', left: 10 }}><Icon.Search c={t.placeholder} size={14}/></span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={tr('betreuung.list.search.placeholder')}
            style={{
              fontSize: isMobile ? 14 : 12,
              border: `1px solid ${t.border}`,
              borderRadius: 8,
              padding: isMobile ? '10px 12px 10px 32px' : '7px 12px 7px 32px',
              width: isMobile ? '100%' : 280,
              boxSizing: 'border-box',
              background: t.inputBg,
              color: t.text,
              fontFamily: 'Outfit,sans-serif',
              outline: 'none',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)} style={{
              padding: isMobile ? '9px 14px' : '7px 13px', borderRadius: 8,
              border: `1px solid ${filter === f.value ? BRAND : t.border}`,
              background: filter === f.value ? '#fce7f4' : t.card,
              color: filter === f.value ? BRAND : t.text,
              fontSize: isMobile ? 13 : 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'Outfit, sans-serif',
              minHeight: isMobile ? 38 : undefined,
            }}>{tr(f.labelKey)}</button>
          ))}
        </div>
        {/* Zugewiesen an: Mehrfachauswahl, gleiche Komponente wie in /pflege */}
        <AdminMultiFilter
          dark={dark}
          admins={admins}
          value={assignedToFilter}
          onChange={setAssignedToFilter}
          currentAdminId={user?.id ?? null}
        />
        {/* SGB5 aktiv: nur BK, die Behandlungspflege (SGB V) übernehmen */}
        <button
          type="button"
          data-testid="bk-sgb5-filter"
          aria-pressed={sgb5Only}
          aria-label={tr('betreuung.filter.sgb5_aria')}
          onClick={() => setSgb5Only(v => !v)}
          style={{
            padding: isMobile ? '9px 14px' : '7px 13px', borderRadius: 8,
            border: `1px solid ${sgb5Only ? BRAND : t.border}`,
            background: sgb5Only ? '#fce7f4' : t.card,
            color: sgb5Only ? BRAND : t.text,
            fontSize: isMobile ? 13 : 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'Outfit, sans-serif',
            minHeight: isMobile ? 38 : undefined,
          }}
        >{tr('betreuung.filter.sgb5')}</button>
        {/* Dispo: Filter nach Verfügbarkeit / Urlaub */}
        <div data-testid="bk-dispo-filter" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: t.muted, marginRight: 2 }}>{tr('betreuung.availability.filter_label')}:</span>
          {[{ type: 'all' as const, labelKey: 'betreuung.availability.filter_all', color: BRAND }, ...BK_AVAILABILITY_SELECTABLE].map(f => {
            const active = dispoFilter === f.type;
            return (
              <button
                key={f.type}
                data-testid={`bk-dispo-filter-${f.type}`}
                // Eindeutiger Name („Verfügbarkeit: Alle") — der Status-Filter daneben hat ebenfalls „Alle".
                aria-label={`${tr('betreuung.availability.filter_label')}: ${tr(f.labelKey)}`}
                aria-pressed={active}
                onClick={() => setDispoFilter(f.type)}
                style={{
                padding: isMobile ? '9px 14px' : '7px 13px', borderRadius: 8,
                border: `1px solid ${active ? f.color : t.border}`,
                background: active ? `${f.color}1a` : t.card,
                color: active ? f.color : t.text,
                fontSize: isMobile ? 13 : 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'Outfit, sans-serif',
                minHeight: isMobile ? 38 : undefined,
              }}>{tr(f.labelKey)}</button>
            );
          })}
          {dispoError && <span style={{ fontSize: 11.5, color: '#b42318' }}>{tr('betreuung.availability.load_error')}</span>}
        </div>
        <div style={{
          // Mobile: Aktions-Block in eigene volle Zeile am Ende der Toolbar.
          // Desktop: ganz rechts via marginLeft:auto wie zuvor.
          marginLeft: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : 'auto',
          display: 'flex',
          gap: 8,
          justifyContent: isMobile ? 'flex-end' : undefined,
        }}>
          <ColumnPicker dark={dark} columns={translatedColumns} visible={visibleCols} onChange={setVisibleCols} />
          <Button
            variant="primary"
            size="sm"
            dark={dark}
            icon={<Icon.Plus/>}
            style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
          >{tr('betreuung.list.action.new')}</Button>
        </div>
      </div>

      {/* Zeilen-Aktionen werden nur bei Mouseover der Zeile eingeblendet. */}
      <style>{`
        .bk-row .bk-row-actions { opacity: 0; transition: opacity .12s ease; pointer-events: none; }
        .bk-row:hover .bk-row-actions, .bk-row:focus-within .bk-row-actions { opacity: 1; pointer-events: auto; }
      `}</style>

      {/* Table View — A7: Box ist per Drag (unten rechts) in der Höhe
          verstellbar; die Höhe wird im localStorage gemerkt. */}
      <div
        ref={boxRef}
        data-testid="betreuung-box"
        style={{
          height: boxHeight, minHeight: MIN_STORED_HEIGHT,
          resize: 'vertical', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', flexShrink: 0,
        }}
      >
      <Card dark={dark} style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ overflow: 'auto', flex: 1 }}>
            <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: t.tableHeader, zIndex: 1 }}>
                <tr>
                  {/* Aktionsspalte (Hover-Buttons) — Batch-12: ganz links */}
                  <th
                    aria-label={tr('pflege.list.header.actions')}
                    style={{ padding: `${d.rowY}px ${d.cellX}px`, textAlign: 'left', fontWeight: 600, fontSize: 11, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap', width: 1 }}
                  />
                  {cols.map(col => {
                    const sf = BETREUUNG_SORT_FIELDS[col.id];
                    const active = !!sf && sortField === sf.field && sortProfile === !!sf.profile;
                    return (
                      <th
                        key={col.id}
                        onClick={sf ? () => toggleSort(col.id) : undefined}
                        style={{ padding: `${d.rowY}px ${d.cellX}px`, textAlign: 'left', fontWeight: 600, fontSize: 11, color: active ? t.heading : t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${t.border}`, cursor: sf ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}
                      >
                        <span>{col.label}</span>
                        {active && <span style={{ marginLeft: 4 }}>{sortOrder === 1 ? '▲' : '▼'}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {loading && filtered.length === 0 && (
                  <tr><td colSpan={cols.length + 1} style={{ padding: 20, color: t.muted, fontSize: 12, textAlign: 'center' }}>{tr('betreuung.list.loading')}</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={cols.length + 1} style={{ padding: 20, color: t.muted, fontSize: 12, textAlign: 'center' }}>{tr('betreuung.list.empty')}</td></tr>
                )}
                {filtered.map(c => (
                  <tr
                    key={c.id}
                    className="bk-row"
                    {...rowLinkProps(buildPath('betreuung', c.id.replace(/^BK-/, '')), () => setSelected(c))}
                    style={{ borderBottom: `1px solid ${t.borderSoft}`, cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = t.hover}
                    onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'}
                  >
                    {/* Hover-Aktionen: ganz links, nur bei Mouseover sichtbar (siehe .bk-row CSS). */}
                    <td style={{ padding: `${d.rowY}px ${d.cellX}px`, textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <div className="bk-row-actions" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <AppLink
                          page="betreuung"
                          sub={c.id.replace(/^BK-/, '')}
                          tab="profildaten"
                          stopPropagation
                          title={tr('pflege.list.action.edit_row')}
                          aria-label={tr('pflege.list.action.edit_row')}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: `1px solid ${t.border}`, borderRadius: 7, background: t.inputBg, cursor: 'pointer', textDecoration: 'none' }}
                        >
                          <Icon.Edit c={t.muted} />
                        </AppLink>
                      </div>
                    </td>
                    {cols.map(col => (
                      <td key={col.id} style={{ padding: `${d.rowY}px ${d.cellX}px` }}>
                        {renderCaregiverCell(c, col.id, dark, t, tr)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Pagination-Footer: Zähler + "Mehr laden" (serverseitig, beide Ansichten). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: t.muted }}>
          {dispoActive
            ? tr('betreuung.list.count', { shown: filtered.length, total: dispoRows.length })
            : tr('betreuung.list.count', { shown: items.length, total })}
        </span>
        {!dispoActive && page < lastPage && (
          <Button variant="secondary" size="sm" dark={dark} disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? tr('betreuung.list.loading') : tr('betreuung.list.load_more')}
          </Button>
        )}
      </div>

      {selected && <CaregiverDetail item={selected} onClose={() => setSelected(null)} dark={dark} isMobile={isMobile}/>}
    </div>
  );
}
